import type React from "react";

import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";

import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiService } from "@/services/api-service";

export const Route = createFileRoute("/auth/_layout/reset-password")({
  component: ResetPassword,
  validateSearch: (search) => ({
    token: (search.token as string) || undefined,
  }),
});

function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { token } = Route.useSearch();

  const { isLoading, isError } = useQuery({
    queryKey: ["reset-token", token],
    queryFn: () => ApiService.validateToken(token),
  });

  const resetPassMutation = useMutation({
    mutationFn: () => ApiService.resetPassword(token, password),
  });

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    resetPassMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2">Validating your request...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-full">
        <AlertCircle className="h-8 w-8 text-red-500" />
        <span className="ml-2">Invalid or expired token</span>
      </div>
    );
  }

  return (
    <div className="max-w-md w-full">
      <Card className=" shadow-2xl rounded-4xl">
        {resetPassMutation.isSuccess && (
          <p className="text-sm text-green-600 font-satoshi text-center">
            Password update successfully,{" "}
            <Link
              to={"/auth/login"}
              className="underline text-xs text-secondary-600"
            >
              Login
            </Link>
          </p>
        )}
        <CardHeader className="space-y-1">
          <CardTitle className="text-base font-bold">Reset Password</CardTitle>
          <CardDescription>Enter your new password below.</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleResetPassword}>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="text-xs"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                    <span className="sr-only">
                      {showPassword ? "Hide password" : "Show password"}
                    </span>
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="text-xs"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                    <span className="sr-only">
                      {showConfirmPassword ? "Hide password" : "Show password"}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <Button
                type="submit"
                className="w-full bg-blue-500 mt-3 hover:bg-blue-600 text-white flex items-center justify-center gap-2 font-satoshi"
                disabled={resetPassMutation.isPending}
              >
                {resetPassMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Reset Password"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
